import { useTranslations } from "next-intl";
import * as React from "react";

import { Icon } from "@zapier/zinnia-icons";
import { Spinner, TextInput } from "@zapier/design-system";

import ClearSelectionButton from "./ClearSelectionButton";
import { useDebounce } from "@zapier/reporting/src/hooks";

type SearchInputProps = {
  isFullWidth?: boolean;
  isSearching: boolean;
  onChange: (value: string) => void;
  value: string | undefined;
};

type RenderIconType = {
  iconSize: number;
  isFocused: boolean;
  isHovered: boolean;
};

const SearchInput = (props: SearchInputProps) => {
  const t = useTranslations("history-page.search-input");
  const [value, setValue] = React.useState(props.value || "");
  const debouncedValue = useDebounce(value, 500);

  React.useEffect(() => {
    if (props.value !== value) {
      setValue(props.value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  React.useEffect(() => {
    // Only fire if there is a real change
    if (debouncedValue !== props.value) {
      props.onChange(debouncedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return (
    <TextInput
      aria-label={t("aria-label")}
      autoComplete="off"
      isFullWidth={props.isFullWidth}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        setValue(e.currentTarget.value)
      }
      placeholder={t("placeholder")}
      renderIconAfter={({ isFocused, isHovered }: RenderIconType) =>
        value.length > 0 && (
          <ClearSelectionButton
            clearSelection={() => setValue("")}
            isFocused={isFocused}
            isHovered={isHovered}
          />
        )
      }
      renderIconBefore={({ iconSize }: RenderIconType) => {
        if (props.isSearching) {
          return <Spinner />;
        }
        return <Icon name="navSearch" size={iconSize} />;
      }}
      value={value}
    />
  );
};

export default SearchInput;
